'use client';
import { type ParkingHistory, type Reservation } from '@parkingbase/ecommerce-api-demo';


import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { differenceInMinutes, format } from 'date-fns';
import { useParkingHistoryStore } from '@/stores/parking-history.store';
import { useReservationStore } from '@/stores/reservation.store';
import { useAuthStore } from '@/stores/user.store';
import { formatDurationShort } from '@/lib/date-time-utils';
import { isViewPassEnabledForRow } from '@/lib/view-pass';
import { MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';

import { mapParkingHistoryToReservation } from '@/lib/parking-history-utils';

export type SessionState = 'active' | 'upcoming' | 'expired';

export interface ActiveSession {
  id: string;
  type: 'ticket' | 'reservation';
  sessionState: SessionState;
  garageName: string;
  vehicleText: string;
  /** Human-readable ticket number (ticket sessions only; reservations show #code in vehicleText). */
  ticketNumber?: string;
  /** Countdown, elapsed, or enter-window text */
  timeText: string;
  isExpired: boolean;
  /**
   * How the "View" action behaves:
   * - 'navigate' → route to `href` (touchless n/a here; monthly→manage-permit, event→reservations)
   * - 'pass'     → open the View Pass modal with `passReservation` (transient parking-history ticket)
   */
  viewMode: 'navigate' | 'pass';
  href: string;
  /** Reservation payload for the View Pass modal (set when viewMode === 'pass'). */
  passReservation?: Reservation;
  /** Minutes remaining until exit time (undefined = no exit time / expired / upcoming) */
  minutesRemaining?: number;
  /** Amount due formatted (e.g. "$12.50") */
  amountDue?: string;
}

export interface ActiveSessionIndicatorData {
  count: number;
  mobileText: string;
  desktopText: string;
  desktopGarageName?: string;
  /** Ticket number for a single ticket session, formatted as #<number>. */
  desktopTicketText?: string;
  desktopTimeText?: string;
  isExpired: boolean;
  href?: string;
  ariaLabel: string;
}

export interface ActiveSessionTimerResult {
  sessions: ActiveSession[];
  indicator: ActiveSessionIndicatorData | null;
}

const TIMER_INTERVAL_MS = 60_000;

function computeTimeText(
  enterTime: string | number | undefined,
  exitTime: string | number | undefined
): { timeText: string; isExpired: boolean; minutesRemaining?: number } {
  const now = new Date();

  // If exit time exists and is in the future → countdown
  if (exitTime) {
    const exitDate = new Date(exitTime);
    if (exitDate > now) {
      const remaining = differenceInMinutes(exitDate, now);
      return {
        timeText: formatDurationShort(remaining),
        isExpired: false,
        minutesRemaining: remaining,
      };
    }
    // Exit time is in the past → expired
    const overdue = differenceInMinutes(now, exitDate);
    return { timeText: formatDurationShort(overdue), isExpired: true };
  }

  // No exit time → show elapsed since entry
  if (enterTime) {
    const elapsed = differenceInMinutes(now, new Date(enterTime));
    return { timeText: formatDurationShort(elapsed), isExpired: false };
  }

  return { timeText: '', isExpired: false };
}

function formatTimeShort(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return format(new Date(dateStr), 'h:mm a');
}

interface TicketView {
  viewMode: 'navigate' | 'pass';
  href: string;
  passReservation?: Reservation;
}

/**
 * Resolves the "View" behavior for an open ticket:
 * - Monthly / permit ticket → navigate to the specific monthly account (falls
 *   back to the accounts list when the account id is absent).
 * - Event ticket → navigate to the reservations list (events are managed like
 *   reservations).
 * - Otherwise (transient parking-history ticket) → open the View Pass modal,
 *   built from the parking-history record. We do NOT route to the touchless
 *   screen: these are history tickets, and the pass modal renders entirely from
 *   data already in hand (no ticket re-fetch / reCAPTCHA).
 */
function resolveTicketView(item: ParkingHistory): TicketView {
  const ticket = item.ticket;
  if (item.monthly || ticket?.monthly || ticket?.monthlyReplacement) {
    return {
      viewMode: 'navigate',
      href: ticket?.accountId
        ? MY_ACCOUNT_ROUTES.managePermitAccountDetail(ticket.accountId)
        : MY_ACCOUNT_ROUTES.managePermitAccounts,
    };
  }
  if (ticket?.eventId) {
    return { viewMode: 'navigate', href: MY_ACCOUNT_ROUTES.reservations };
  }
  // The garage can withhold the pass. Fall back to navigating rather than dropping the row: the
  // session is still real and the parker still needs a way into it — only the QR is withheld.
  // Judged per row because this chip lists sessions across different garages.
  if (!isViewPassEnabledForRow(item)) {
    return { viewMode: 'navigate', href: MY_ACCOUNT_ROUTES.parkingHistory };
  }
  return {
    viewMode: 'pass',
    href: '',
    passReservation: mapParkingHistoryToReservation(item) as Reservation,
  };
}

/**
 * Resolves the "View" destination for a reservation, mirroring the legacy app:
 * - Monthly reservation → the specific monthly account (falls back to the
 *   accounts list when the account id is absent).
 * - Transient and event reservations → the reservations list (the legacy
 *   ReservationsConnected surface; events render there too).
 */
function resolveReservationHref(res: Reservation): string {
  if (res.reservationType?.toUpperCase() === 'MONTHLY') {
    return res.accountId
      ? MY_ACCOUNT_ROUTES.managePermitAccountDetail(res.accountId)
      : MY_ACCOUNT_ROUTES.managePermitAccounts;
  }
  return MY_ACCOUNT_ROUTES.reservations;
}

/**
 * Hook that discovers active parking sessions (tickets + reservations) and
 * provides display-ready props for the header indicator pill and popup.
 *
 * Shows:
 * - Active tickets (open) with countdown to exit
 * - Active reservations (CHECKIN) with countdown to checkoutBeforeTime
 * - Upcoming reservations (UNUSED, active) with enter window
 *
 * Hides reservations that are linked to a ticket for this user (managed via ticket).
 * Updates every 60 seconds.
 */
export function useActiveSessionTimer(): ActiveSessionTimerResult {
  const t = useTranslations('ActiveSession');
  const user = useAuthStore((s) => s.user);

  const transientHistory = useParkingHistoryStore((s) => s.transientHistory);
  const monthlyHistory = useParkingHistoryStore((s) => s.monthlyHistory);
  const fetchTransientHistory = useParkingHistoryStore((s) => s.fetchTransientHistory);
  const fetchMonthlyHistory = useParkingHistoryStore((s) => s.fetchMonthlyHistory);

  const currentReservations = useReservationStore((s) => s.currentReservations);
  const openTicketResponse = useReservationStore((s) => s.openTicketResponse);
  const getAllReservationsByUser = useReservationStore((s) => s.getAllReservationsByUser);
  const fetchOpenTicketDetails = useReservationStore((s) => s.fetchOpenTicketDetails);

  // Fetch data when user is authenticated
  useEffect(() => {
    if (user) {
      fetchTransientHistory();
      fetchMonthlyHistory();
      getAllReservationsByUser();
      fetchOpenTicketDetails();
    }
  }, [
    user,
    fetchTransientHistory,
    fetchMonthlyHistory,
    getAllReservationsByUser,
    fetchOpenTicketDetails,
  ]);

  // Tick counter to force recomputation every 60s
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), TIMER_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const buildSessions = useCallback((): ActiveSession[] => {
    const sessions: ActiveSession[] = [];

    // Collect reservation IDs that are linked to tickets — these will be hidden
    const linkedReservationIds = new Set<string>();
    const allHistory = [...transientHistory, ...monthlyHistory];
    for (const item of allHistory) {
      if (item.reservationId) {
        linkedReservationIds.add(item.reservationId);
      }
    }

    // ── Active tickets (open) ──────────────────────────────────────────
    const openTicketDocId = openTicketResponse?.ticket?.documentId;
    // Ticket number for the current open ticket comes from /reservation/openTickets
    // (the whitelabel open-ticket response); the parking-history embedded ticket
    // does not reliably carry it for zone/touchless tickets.
    const openTicketNumber =
      openTicketResponse?.ticket?.ticket ||
      openTicketResponse?.ticket?.alternateTicket ||
      undefined;
    const rateOutput = openTicketResponse?.rateOutput;
    const amountDue =
      rateOutput?.rate != null && rateOutput.rate > 0
        ? `$${rateOutput.rate.toFixed(2)}`
        : undefined;

    for (const item of allHistory) {
      if (!item.isOpen) continue;
      const ticket = item.ticket;
      // The touchless screen keys off the ticket/transaction document id. On a
      // ParkingHistory that has no embedded ticket, that id lives in
      // `transactionId` — NOT `item.documentId` (the ParkingHistory's own id,
      // which is never a valid ticket id and 404s the touchless lookup).
      const ticketId = ticket?.documentId || item.transactionId || openTicketDocId || '';
      if (!ticketId) continue;
      const { timeText, isExpired, minutesRemaining } = computeTimeText(
        ticket?.enter,
        ticket?.exit
      );
      const vehicleParts = [ticket?.make, ticket?.color, ticket?.plate].filter(Boolean);
      const view = resolveTicketView(item);
      sessions.push({
        id: ticketId,
        type: 'ticket',
        sessionState: isExpired ? 'expired' : 'active',
        garageName: item.garageName || '',
        vehicleText: vehicleParts.join(' \u00B7 '),
        ticketNumber:
          (ticketId === openTicketDocId ? openTicketNumber : undefined) ||
          ticket?.ticket ||
          ticket?.alternateTicket ||
          item.transaction?.ticketNumber?.toString() ||
          undefined,
        timeText,
        isExpired,
        minutesRemaining,
        amountDue,
        viewMode: view.viewMode,
        href: view.href,
        passReservation: view.passReservation,
      });
    }

    // ── Reservations (active + upcoming) ───────────────────────────────
    for (const res of currentReservations) {
      // Skip reservations linked to a ticket — user manages via ticket
      if (res.reservationId && linkedReservationIds.has(res.reservationId)) continue;

      // Skip cancelled / void / checked-out
      const status = res.status?.toUpperCase();
      if (status === 'CANCELLED' || status === 'VOID' || status === 'CHECKOUT') continue;

      const resId = res.reservationId || '';
      const displayCode = res.vendorReservationId || resId;
      const garageName = res.garageName || '';
      const vehicleText = `${t('reservation')} \u00B7 #${displayCode}`;

      if (status === 'CHECKIN') {
        // Active reservation — countdown to checkoutBeforeTime
        const { timeText, isExpired, minutesRemaining } = computeTimeText(
          res.checkinAfterTime,
          res.checkoutBeforeTime
        );
        sessions.push({
          id: resId,
          type: 'reservation',
          sessionState: isExpired ? 'expired' : 'active',
          garageName,
          vehicleText,
          timeText,
          isExpired,
          minutesRemaining,
          viewMode: 'navigate',
          href: resolveReservationHref(res),
        });
      } else if (res.active) {
        // Upcoming reservation (UNUSED + active) — show enter window
        const enterAfter = res.checkinAfterTime;
        const enterBefore = res.checkinBeforeTime;

        let timeText: string;
        if (enterAfter && enterBefore) {
          timeText = t('enterWindow', {
            start: formatTimeShort(enterAfter),
            end: formatTimeShort(enterBefore),
          });
        } else if (enterAfter) {
          timeText = t('enterAfter', { time: formatTimeShort(enterAfter) });
        } else {
          timeText = '';
        }

        sessions.push({
          id: resId,
          type: 'reservation',
          sessionState: 'upcoming',
          garageName,
          vehicleText,
          timeText,
          isExpired: false,
          viewMode: 'navigate',
          href: resolveReservationHref(res),
        });
      }
    }

    return sessions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transientHistory, monthlyHistory, currentReservations, openTicketResponse, tick, t]);

  const sessions = useMemo(() => buildSessions(), [buildSessions]);

  const indicator = useMemo((): ActiveSessionIndicatorData | null => {
    if (sessions.length === 0) return null;

    const count = sessions.length;

    if (count === 1) {
      const session = sessions[0];
      const hasCountdown = session.timeText !== '';

      return {
        count: 1,
        mobileText: session.isExpired
          ? t('expired')
          : hasCountdown
            ? session.timeText
            : session.sessionState === 'upcoming'
              ? t('upcoming')
              : t('active'),
        desktopText: session.isExpired
          ? t('expired')
          : session.sessionState === 'upcoming'
            ? t('upcoming')
            : t('active'),
        desktopGarageName: session.garageName,
        desktopTicketText: session.ticketNumber ? `#${session.ticketNumber}` : undefined,
        desktopTimeText: session.isExpired
          ? t('ago', { duration: session.timeText })
          : session.timeText,
        isExpired: session.isExpired,
        href: session.href,
        ariaLabel: t('activeSessionLabel', { garage: session.garageName }),
      };
    }

    // Multiple sessions
    const anyExpired = sessions.some((s) => s.isExpired);
    return {
      count,
      mobileText: t('countActive', { count }),
      desktopText: t('countActiveSessions', { count }),
      isExpired: anyExpired,
      ariaLabel: t('activeSessionCountLabel', { count }),
    };
  }, [sessions, t]);

  return { sessions, indicator };
}
